import React, { useState, useEffect } from "react";
import BotInfo from "./BotInfo";
import GlobalStats from "./GlobalStats";
import GameHistory from "./GameHistory";
import MetricDisplay from "./MetricDisplay";
import WinrateGrid from "./WinrateGrid";
import TournamentRecords from "./TournamentRecords";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useUser } from "../UserContext";
import ConfirmationModal from "../common/ConfirmationModal";
import AlertModal from "../common/AlertModal";
import {format} from "date-fns";
import { encodeSeed, decodeSeed } from "../seedUtils";
import SeedInput from "../forms/SeedInput";
import {API_URL} from "../../App";


const getIconPath = (bot) => {
  return `/icons/${bot.character}_${bot.costume_id}.webp`;
};

const getRankStyle = (botInfo) => {
  switch (botInfo?.metrics?.rank) {
    case 1:
      return "text-3xl font-bold text-yellow-500"; // Gold
    case 2:
      return "text-2xl font-bold text-gray-300"; // Silver
    case 3:
      return "text-2xl font-bold text-yellow-700"; // Bronze
    default:
      return "text-md font-medium text-white"; // Default rank color
  }
};

const getRankText = (botInfo) => {
  if (botInfo?.bot?.is_out) {
    return "Out!";
  } else if (botInfo?.metrics?.rank === 0) {
    return "#???";
  } else {
    return `#${botInfo?.metrics?.rank}`;
  }
};

const BotMenu = () => {
  const { user } = useUser();
  const { twitchDisplayName, botTag } = useParams();
  const [seed, setSeed] = useState("0"*30)
  const [selectedSection, setSelectedSection] = useState("Bot Info");
  const [botInfo, setBotInfo] = useState(null); // Start with null to handle loading state
  const [loading, setLoading] = useState(true); // Loading state
  const [showConfirmKill, setShowConfirmKill] = useState(false);
  const [showKillSuccess, setShowKillSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Fetch bot info from the backend
    const fetchBotInfo = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/bot-info/${botTag}`);
        setBotInfo(response.data);
        setSelectedSection("Bot Info");
        const seed = encodeSeed(response.data.bot.character, response.data.bot.costume_id, response.data.bot.preferred_stage_index, response.data.bot.preferred_move_id, response.data.bot.stats)
        setSeed(seed);
        //console.log("encoded", response.data.bot.character, response.data.bot.costume_id, response.data.bot.preferred_stage_index, response.data.bot.preferred_move_id, response.data.bot.stats)
        //console.log("decoded", decodeSeed(seed));

      } catch (error) {
        //console.error("Failed to fetch bot from tag", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBotInfo();
  }, [botTag, user]);

  // Function to handle killing the bot
  const handleKillBot = async () => {
    if (!botInfo || user.id !== botInfo?.bot?.twitch_user_id) return;

    const token = localStorage.getItem("twitchToken");

    try {
      await axios.post(
        `${API_URL}/api/kill-bot/${botTag}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setShowConfirmKill(false);
      setShowKillSuccess(true)
    } catch (error) {
    }
  };

  const handleKillBotExit = () => {
    setShowKillSuccess(false);
    navigate("/");
  }
 
  // Function to handle resubmitting the bot
  const handleResubmitBot = () => {
    if (!botInfo) return;
    
    const botConfig = {
      tag: botInfo.bot?.tag,
      character: botInfo.bot?.character,
      preferred_move: botInfo.bot?.preferred_move,
      preferred_stage: botInfo.bot?.preferred_stage,
      costume_id: botInfo.bot?.costume_id,
      stats: JSON.stringify(botInfo.bot?.stats),
    };

    const queryParams = new URLSearchParams(botConfig).toString();
    navigate(`/submit-seed?${queryParams}`);
  };

  // Function to refresh the bot info
  const handleRefreshBot = async () => {
    setLoading(true); // Show loading state while fetching data
    try {
      const response = await axios.get(`${API_URL}/api/bot-info/${botTag}`);
      setBotInfo(response.data);
    } catch (error) {
      //console.error("Failed to fetch bot info for refresh", error);
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    "Bot Info",
    "Global Stats",
    "Game History",
    "Progression",
    "RL Metrics",
    "Detailed Winrates",
    "Tournament Records",
  ];

  // Handle loading state
  if (loading) {
    return <div className="text-white text-center mt-20">Loading...</div>;
  }

  // Handle error state (when botInfo is null after loading)
  if (!botInfo) {
    return (
      <div className="text-center mt-20">
        <p>Failed to load bot information.</p>
        <button
          onClick={() => navigate("/")}
          className="mt-4 py-2 px-4 text-white rounded-lg bg-pink-300/10 border border-pink-300/30 rounded-lg hover:border-pink-300 duration-200 ease-in-out"
        >
          Go Back
        </button>
      </div>
    );
  }

  const currTime = new Date(botInfo?.bot?.time_created)

  return (
    <div
      style={{
        background: `linear-gradient(to top, ${botInfo?.hue || "#000"}30 10%, rgba(0, 0, 0, 0.0))`,
      }}
      className="flex h-screen px-2 py-2 flex-col"
    >
      {/* Header */}
      <div
        className="w-full left-0 bg-gray-900 bg-opacity-60 py-3"
        style={{
          background: `linear-gradient(to right, rgba(0, 0, 0, 0.0), ${
            botInfo?.hue || "#000"
          }50, rgba(0, 0, 0, 0.0))`,
        }}
      >
        <div className="max-w-5xl mx-auto grid grid-cols-3 items-center">
          <div className="hidden md:block"></div>
          <div className="flex flex-col items-center text-center space-y-2">
            <img
                src={getIconPath(botInfo?.bot)}
                alt=""
                className="w-8 h-8 border border-gray-300 rounded-full mr-2 z-10"
              />
            <div>
            <a className="text-4xl font-bold mr-3">{botTag}</a>
              <a className={getRankStyle(botInfo)}>
                {getRankText(botInfo)}
              </a>
            </div>
            <p className="text-lg text-gray-200">
              Created by <a className="text-gray-400 cursor-pointer hover:text-gray-100" onClick={() => navigate(`/profiles/${twitchDisplayName}`)}>@{twitchDisplayName || "Unknown"} </a>
            </p>
            <p className="text-xs text-gray-300">
              (on {format(currTime, 'yyyy-MM-dd HH:mm:ss z')|| "Unknown Date"}) </p>
            <SeedInput value={seed} onChange={() => {}} pasteMode={false} color={botInfo ? botInfo.hue : "cyan"} />
          </div>
          <div className="flex flex-col items-end space-y-2">
            {user?.id === botInfo?.bot?.twitch_user_id && (
              <>
                {!botInfo?.bot?.is_out && (
                  <button
                    onClick={() => {setShowConfirmKill(true)}}
                    className="border-2 bg-red-400/10 border-red-400/40 text-white py-2 px-4 rounded-lg hover:border-red-400 mb-2 transition-all duration-200 ease-in-out"
                  >
                    Kill Bot
                  </button>
                )}
                {/*{botInfo?.bot?.is_out && (
                  <button
                    onClick={handleResubmitBot}
                    className="border-2 bg-gray-500/10 border-gray-500/40 text-white py-2 px-4 rounded-lg hover:border-gray-500 duration-200 transition-all"
                  >
                    Reuse seed
                  </button>
                )}
                */}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex max-w-7xl mx-auto mt-2">
        <div className="w-1/4 flex flex-col items-start space-y-4 pt-16">
          {menuItems.map((item) => (
            <button
              key={item}
              onClick={() => setSelectedSection(item)}
              className={`w-full py-2 px-4 text-left border border-pink-300/30 rounded-lg hover:border-pink-300 focus:outline-none duration-200 ease-in-out ${
                selectedSection === item ? "bg-gray-800/60" : "bg-gray-950/60"
              }`}
            >
              {item}
            </button>
          ))}
          {/* Refresh button (SVG) */}
          <div className="bg-gray-950/50 rounded-full p-1 mt-4 flex mx-auto">
          <button onClick={handleRefreshBot} className="flex mx-auto hover:scale-110 transition-transform duration-200 ease-in-out">
            <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            className="w-9 h-9 cursor-pointer text-gray-300/80 hover:text-pink-400/80 transition-colors duration-200 ease-in-out"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="1.2"
            >
              <circle class="cls-1" cx="12" cy="10.09" r="1.91"/><path class="cls-1" d="M9.14,14.86h0A2.86,2.86,0,0,1,12,12h0a2.86,2.86,0,0,1,2.86,2.86h0"/><path class="cls-1" d="M18,3.41A10.5,10.5,0,0,1,12,22.5"/><path class="cls-1" d="M6,20.59A10.5,10.5,0,0,1,12,1.5"/><polyline class="cls-1" points="17.73 8.18 17.73 3.41 22.5 3.41"/><polyline class="cls-1" points="6.27 15.82 6.27 20.59 1.5 20.59"/>
            </svg>
          </button>
          </div>
        </div>
        
        <div className="flex flex-col px-4 p-4 min-w-[600px] max-w-[600px]  max-h-[740px]">
          <div className="flex-1 bg-gray-950/50 rounded-lg overflow-y-auto">
            {selectedSection === "Bot Info" && <BotInfo botInfo={botInfo} />}
            {selectedSection === "Global Stats" && <GlobalStats botInfo={botInfo} />}
            {selectedSection === "Game History" && <GameHistory botInfo={botInfo} />}
            {selectedSection === "Progression" && <MetricDisplay botInfo={botInfo} type="progression" title="Progression" />}
            {selectedSection === "RL Metrics" && <MetricDisplay botInfo={botInfo} type="rl" title="RL metrics" />}
            {selectedSection === "Detailed Winrates" && <WinrateGrid botInfo={botInfo} />}
            {selectedSection === "Tournament Records" && (
              <TournamentRecords />
            )}
          </div>
        </div>
      </div>
      {showConfirmKill && <ConfirmationModal message={"Are you sure you want to kill this bot?"} onConfirm={handleKillBot} onCancel={() => {setShowConfirmKill(false)}} />}
      {showKillSuccess && <AlertModal success={true} message="Your bot was successfully killed." onClose={handleKillBotExit} />}

    </div>
  );
};

export default BotMenu;
